// Tiny Web Audio chime kit — no mp3 ships with the bundle, every alert is
// synthesised on the fly. The kitchen (new_order) chime plays loud (0.9
// peak) since KDS runs on a screen away from the cashier in a noisy
// kitchen — it needs to cut through. Other chimes stay gentler (0.35).
//
// User preferences are read from localStorage (the same `ofi-cia.notifs`
// blob the Settings page writes). When the matching toggle is OFF the
// chime is a no-op — so any caller can fire-and-forget without checking.

export type ChimeKind =
  | 'new_order'      // KDS ticket arrives / admin sees a fresh order
  | 'online_order'   // a customer placed an order from the OFI Eats app / QR — needs accepting
  | 'order_ready'    // kitchen marked the order ready — sales person hears it
  | 'sale'           // POS payment captured / order rung up
  | 'low_stock'      // inventory hits reorder threshold
  | 'payout_paid';   // investor payout completes

const PREF_KEY = 'ofi-cia.notifs';

/** Lazy AudioContext — created on first use, kept warm. */
let ac: AudioContext | null = null;
function ctx(): AudioContext | null {
  if (ac) return ac;
  try {
    const AC = window.AudioContext
      || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    ac = new AC();
  } catch { ac = null; }
  return ac;
}

interface Prefs {
  new_order?: boolean;
  order_ready?: boolean;
  low_stock?: boolean;
  payout_paid?: boolean;
  sale?: boolean;
}

function readPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(PREF_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

/**
 * Map a chime kind to the preference key that gates it. Sale is always on
 * by default because cashiers expect audible feedback on a successful sale.
 */
function isEnabled(kind: ChimeKind): boolean {
  const p = readPrefs();
  switch (kind) {
    case 'new_order':   return p.new_order   !== false;
    // Same switch as new_order: one "new order sound" toggle in Settings.
    case 'online_order': return p.new_order  !== false;
    case 'order_ready': return p.order_ready !== false;
    case 'low_stock':   return p.low_stock   !== false;
    case 'payout_paid': return p.payout_paid !== false;
    case 'sale':        return p.sale        !== false;
  }
}

/** Tone descriptor — frequency in Hz, duration in ms, optional shape. */
type Tone = { f: number; ms: number; type?: OscillatorType };

/** Play a sequence of tones with small gaps. Returns once scheduled. */
function play(tones: Tone[], peak = 0.35) {
  const c = ctx();
  if (!c) return;
  // Some browsers suspend the context until a user gesture. Resume best-effort.
  if (c.state === 'suspended') c.resume().catch(() => {});

  let t = c.currentTime;
  for (const tone of tones) {
    const osc  = c.createOscillator();
    const gain = c.createGain();
    osc.type = tone.type ?? 'sine';
    osc.frequency.setValueAtTime(tone.f, t);

    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(peak, t + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + tone.ms / 1000);

    osc.connect(gain).connect(c.destination);
    osc.start(t);
    osc.stop(t + tone.ms / 1000 + 0.02);

    t += tone.ms / 1000 + 0.03;
  }
}

/** Peak gain per chime kind — new_order runs loud since KDS sits on a
 *  screen away from the cashier in a noisy kitchen and needs to cut through. */
const PEAK: Record<ChimeKind, number> = {
  new_order:   0.9,
  online_order: 0.9,
  order_ready: 0.5,
  sale:        0.35,
  low_stock:   0.35,
  payout_paid: 0.35,
};

/**
 * Pre-baked chime patterns. Each one has a unique melody so an operator
 * who's heard the KDS pop a few times can tell what fired without looking.
 */
const CHIMES: Record<ChimeKind, Tone[]> = {
  new_order:   [{ f: 880, ms: 90 }, { f: 1175, ms: 140 }],                            // ding-doh
  // Longer rising doorbell. It repeats until someone accepts the order, so it
  // has to be unmistakable and different from the kitchen's ding-doh.
  online_order: [{ f: 784, ms: 130 }, { f: 988, ms: 130 }, { f: 1175, ms: 130 }, { f: 1568, ms: 340 }],
  // Distinct rising 3-tone — different enough from new_order so the
  // salesperson never confuses an arriving order with a ready one.
  order_ready: [{ f: 988, ms: 80 }, { f: 1318, ms: 80 }, { f: 1568, ms: 220 }],        // ready-bell
  sale:        [{ f: 1320, ms: 70 }, { f: 1760, ms: 70 }, { f: 1975, ms: 220 }],      // ka-ching!
  low_stock:   [{ f: 660, ms: 120 }, { f: 660, ms: 120 }],                            // double low beep
  payout_paid: [{ f: 1046, ms: 100 }, { f: 1318, ms: 100 }, { f: 1568, ms: 180 }],   // bright triad up
};

/**
 * Public API: play a chime if the user has it enabled.
 * Safe to call from anywhere — silent when prefs are off or audio is blocked.
 */
export function playChime(kind: ChimeKind): void {
  if (!isEnabled(kind)) return;
  try { play(CHIMES[kind], PEAK[kind]); } catch { /* swallow */ }
}

/**
 * True once the browser lets us make sound. False until the page has had a
 * click / key press (autoplay policy) — e.g. a tablet that was just reloaded —
 * so alerts can tell staff to tap the screen instead of failing silently.
 */
export function audioUnlocked(): boolean {
  try { return ctx()?.state === 'running'; } catch { return false; }
}

/**
 * Browsers suspend AudioContext until the user interacts with the page.
 * Call this once on app boot — the first pointer/keyboard event will warm
 * up the context so the very next chime can play without lag.
 */
export function primeAudioOnFirstGesture(): void {
  if (typeof window === 'undefined') return;
  const handler = () => {
    try { ctx()?.resume?.(); } catch { /* ignore */ }
    window.removeEventListener('pointerdown', handler);
    window.removeEventListener('keydown',     handler);
  };
  window.addEventListener('pointerdown', handler, { once: true });
  window.addEventListener('keydown',     handler, { once: true });
}
